/**
 * @name: Product Recommendations
 * @author: Cursor (Agent)
 * @date: 2026-09-25
 * @description: Fetches Shopify native recommendations and initializes the Slick slider
 * @strict-mode: true
 */

window._theme = window._theme || {};

if (typeof window._theme.alignRecommendationSlider !== 'function') {
  /**
   * @param {object} slick
   * @returns {void}
   */
  window._theme.alignRecommendationSlider = (slick) => {
    if (!slick?.$slideTrack?.length) return;

    const slidesToShow = Number(slick.options.slidesToShow) || 1;

    if (slick.slideCount > Math.ceil(slidesToShow)) return;

    slick.$slideTrack.css({
      transform: 'translate3d(0px, 0px, 0px)',
      marginLeft: 0,
    });
  };
}

/**
 * @extends {HTMLElement}
 */
class ProductRecommendations extends HTMLElement {
  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;

    this.#initialized = true;

    if (this.#hasRenderedProducts()) {
      this.hidden = false;
      this.#initSlider();
    }

    this.#fetchRecommendations();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#destroySlider();
  }

  /**
   * @returns {boolean}
   */
  #hasRenderedProducts() {
    return Boolean(this.querySelector('[data-related-products-list] .product-item'));
  }

  /**
   * @returns {Promise<void>}
   */
  async #fetchRecommendations() {
    const url = this.dataset.url;

    if (!url) {
      if (!this.#hasRenderedProducts()) {
        this.hidden = true;
      }
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        if (!this.#hasRenderedProducts()) {
          this.hidden = true;
        }
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('product-recommendations');

      if (!source || !source.querySelector('[data-related-products-list] .product-item')) {
        if (!this.#hasRenderedProducts()) {
          this.hidden = true;
        }
        return;
      }

      this.#destroySlider();
      this.innerHTML = source.innerHTML;
      this.hidden = false;
      this.#initSlider();
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      if (!this.#hasRenderedProducts()) {
        this.hidden = true;
      }
    }
  }

  /**
   * @returns {void}
   */
  #destroySlider() {
    const slider = this.querySelector('[data-related-products-list]');

    if (!slider || !window.jQuery) return;

    const $slider = window.jQuery(slider);

    if ($slider.hasClass('slick-initialized')) {
      $slider.slick('unslick');
    }
  }

  /**
   * @returns {void}
   */
  #initSlider() {
    const slider = this.querySelector('[data-related-products-list]');

    if (!slider) return;

    const columns = Number(this.dataset.columns) || 5;
    const prevLabel = this.dataset.prevLabel || 'Previous';
    const nextLabel = this.dataset.nextLabel || 'Next';

    const init = () => {
      if (!slider.isConnected) return;

      if (!window._theme || typeof window._theme.initSlickSlider !== 'function' || !window.jQuery) {
        setTimeout(init, 50);
        return;
      }

      const $slider = window.jQuery(slider);

      if ($slider.hasClass('slick-initialized')) {
        return;
      }

      window._theme.initSlickSlider(
        $slider,
        {
          rows: 0,
          dots: false,
          arrows: true,
          accessibility: true,
          infinite: false,
          mobileFirst: true,
          speed: 400,
          slidesToShow: 1.5,
          slidesToScroll: 1,
          prevArrow: `<button class="slick-prev slick-arrow" aria-label="${prevLabel}" type="button"><i class="arrow1 left1"></i><span class="sr-only">${prevLabel}</span></button>`,
          nextArrow: `<button class="slick-next slick-arrow" aria-label="${nextLabel}" type="button"><i class="arrow1 right1"></i><span class="sr-only">${nextLabel}</span></button>`,
          responsive: [
            {
              breakpoint: 767,
              settings: {
                slidesToShow: 3,
                slidesToScroll: 3,
              },
            },
            {
              breakpoint: 991,
              settings: {
                slidesToShow: columns,
                slidesToScroll: columns,
              },
            },
          ],
        },
        (slick) => {
          if (!slick || !slick.$slider) return;

          slick.$slider[0].querySelectorAll('.slick-slide').forEach((slide) => {
            slide.removeAttribute('tabindex');
          });

          if (typeof window._theme?.alignRecommendationSlider === 'function') {
            window._theme.alignRecommendationSlider(slick);
            slick.$slider
              .off('setPosition.alignLeft')
              .on('setPosition.alignLeft', () => window._theme.alignRecommendationSlider(slick));
          }
        }
      );
    };

    init();
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
